import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { LabelKeyToProofType, ProofTypeLabelKeys } from 'src/app/core/_enums/proofTypes.code';

interface FileToUpload {
  file: File;
  form: FormGroup;
}

interface SelectableResource {
  id: string;
  displayName: string;
  type: 'input' | 'output' | 'vehicle';
}

@Component({
  selector: 'app-dialog-upload-justificatives',
  templateUrl: './dialog-upload-justificatives.component.html',
  styleUrls: ['./dialog-upload-justificatives.component.scss']
})
export class DialogUploadJustificativesComponent {
  filesToUpload: FileToUpload[] = [];
  justificationOptions: string[] = Object.values(ProofTypeLabelKeys);
  availableResources: SelectableResource[] = [];
  isDragging = false;

  constructor(
    public dialogRef: MatDialogRef<DialogUploadJustificativesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.prepareResources();
  }

  private prepareResources(): void {
    const operation = this.data.operation;
    if (!operation?.detailedOperation) {
      return;
    }

    const inputs: SelectableResource[] = (operation.detailedOperation.inputProducts || []).map((p: any) => ({
      id: p.operationCompositionId!,
      displayName: p.displayName || this.translate.instant('unknown_input_product'),
      type: 'input'
    }));

    const outputs: SelectableResource[] = (operation.detailedOperation.outputProducts || []).map((p: any) => ({
      id: p.operationCompositionId!,
      displayName: p.displayName || this.translate.instant('unknown_output_product'),
      type: 'output'
    }));

    const vehicles: SelectableResource[] = (operation.detailedOperation.vehicles || []).map((v: any) => ({
      id: v.materialCompositionId!,
      displayName: v.displayName || this.translate.instant('unknown_vehicle'),
      type: 'vehicle'
    }));

    this.availableResources = [...inputs, ...outputs, ...vehicles];
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFiles(input.files);
    }
    input.value = '';
  }

  private processFiles(files: FileList): void {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.filesToUpload.push({
        file,
        form: this.fb.group({
          resourceIds: [[], Validators.required],
          justificationTypes: [[], Validators.required],
          comment: ['']
        })
      });
    }
  }

  removeFile(index: number): void {
    this.filesToUpload.splice(index, 1);
  }

  onSave(): void {
    if (!this.isFormValid()) {
      return;
    }

    const justificatives = this.filesToUpload.map((item) => {
      const formValue = item.form.value;

      const justificationTypeEnums = formValue.justificationTypes.map((displayName: string) => LabelKeyToProofType[displayName]);

      const selectedResourceIds: string[] = formValue.resourceIds;
      const proofLinks = {
        inputProductProoved: [] as string[],
        outputProductProoved: [] as string[],
        vehicleProoved: [] as string[]
      };

      selectedResourceIds.forEach((id) => {
        const resource = this.availableResources.find((r) => r.id === id);
        if (resource) {
          if (resource.type === 'input') proofLinks.inputProductProoved.push(id);
          if (resource.type === 'output') proofLinks.outputProductProoved.push(id);
          if (resource.type === 'vehicle') proofLinks.vehicleProoved.push(id);
        }
      });

      return {
        proovedTypes: justificationTypeEnums,
        comment: formValue.comment,
        fileName: item.file.name,
        fileToUpload: item.file,
        createDate: new Date(),
        ...proofLinks
      };
    });

    this.dialogRef.close(justificatives);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isFormValid(): boolean {
    if (this.filesToUpload.length === 0) {
      return false;
    }
    return this.filesToUpload.every((item) => item.form.valid);
  }

  createFileUrl(file: File): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }
}
